import { useState, useEffect } from "react";
import { type BookingType, CallState } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { useResendEmail } from "@tenant/self-serve";
import {
  type Form,
  requiredEmail,
  useForm,
  useFormErrorHelper,
} from "@/utils/form";

export type ResendConfirmationEmailFormKey = "email";
export type ResendConfirmationEmailForm = Form<ResendConfirmationEmailFormKey>;

const validate =
  (i18nModule: string) => (state: ResendConfirmationEmailForm["state"]) => {
    const nextErrors: ResendConfirmationEmailForm["state"]["errors"] =
      Object.values(state.values).filter(Boolean).length > 0
        ? {
            email: requiredEmail(i18nModule, state.values, "email"),
          }
        : {};

    return nextErrors;
  };

export type UseResendConfirmationEmailProps = {
  reservationBookingId: string;
  bookingEmail: string;
  bookingType: BookingType;
  onSuccess?: () => void;
};

export const useResendConfirmationEmail = ({
  reservationBookingId,
  bookingEmail,
  bookingType,
  onSuccess,
}: UseResendConfirmationEmailProps) => {
  const { t } = useI18nContext();
  const [hasError, setHasError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const { context, handlers } = useResendEmail(
    reservationBookingId,
    bookingType
  );

  const form = useForm<ResendConfirmationEmailFormKey>({
    validate: validate("resendConfirmationEmail.form"),
    initialValues: {
      email: bookingEmail ?? "",
    },
  });

  const getFieldErrorText = useFormErrorHelper(form.state, t);

  useEffect(() => {
    if (context.reqState === CallState.Failed) {
      setHasError(true);
      setIsSuccess(false);
    }
    if (context.reqState === CallState.Success) {
      setIsSuccess(true);
      setHasError(false);
      onSuccess?.();
    }
  }, [context.reqState, onSuccess]);

  const onSubmit = () => {
    if (form.state.isValid) {
      handlers.onSubmit(form.state.values["email"]);
    }
  };

  const clearError = () => {
    setHasError(false);
    setIsSuccess(false);
  };

  const reset = () => {
    setHasError(false);
    setIsSuccess(false);
    handlers.setReqState(CallState.NotCalled);
  };

  return {
    // Form state
    form,
    getFieldErrorText,

    // Loading and success/error states
    isLoading: context.reqState === CallState.InProcess,
    hasError,
    isSuccess,

    // Actions
    onSubmit,
    clearError,
    reset,

    // Computed values
    isFormValid: form.state.isValid && form.state.values.email != null,
  };
};
